/**
 * Restore Purchases Button for iOS
 * Allows users to restore previous IAP subscriptions
 */

import React, { useState } from 'react';
import { isIAPAvailable, restorePurchases, getReceiptData, validateReceipt } from '../services/iapService';
import API_BASE_URL from '../config';

interface RestorePurchasesButtonProps {
  onSuccess?: () => void;
}

const RestorePurchasesButton: React.FC<RestorePurchasesButtonProps> = ({ onSuccess }) => {
  const [restoring, setRestoring] = useState(false);

  if (!isIAPAvailable()) {
    return null; // Only show on iOS
  }

  const handleRestore = async () => {
    if (restoring) return;

    try {
      setRestoring(true);

      // Check if user owns any products BEFORE calling restore
      // (restore might clear the ownership status temporarily)
      const { CdvPurchase } = window;
      if (!CdvPurchase) {
        alert('IAP not available');
        return;
      }

      const { store } = CdvPurchase;
      const productsBeforeRestore = [
        store.get('com.wanderingsock.annual.basic'),
        store.get('com.wanderingsock.annual.pro'),
        store.get('com.wanderingsock.annual.business'),
      ].filter(Boolean);

      const ownedBeforeRestore = productsBeforeRestore.filter(p => p.owned);
      console.log('[RestorePurchases] Owned before restore:', ownedBeforeRestore.map(p => p.id));

      const result = await restorePurchases();

      if (!result.success) {
        alert(result.error || 'Failed to restore purchases');
        return;
      }

      // Wait a bit for ownership status to update
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Check again after restore
      const products = [
        store.get('com.wanderingsock.annual.basic'),
        store.get('com.wanderingsock.annual.pro'),
        store.get('com.wanderingsock.annual.business'),
      ].filter(Boolean);

      console.log('[RestorePurchases] Products after restore:', products.map(p => ({
        id: p.id,
        owned: p.owned,
        state: p.state
      })));

      // Use products owned before restore if current check fails
      const ownedProduct = products.find(p => p.owned) || ownedBeforeRestore[0];
      
      if (ownedProduct) {
        // Verify this purchase belongs to the current app user
        const ownerKey = 'iap_purchase_owner';
        const owners: Record<string, string> = JSON.parse(localStorage.getItem(ownerKey) || '{}');
        const token = localStorage.getItem('auth_token');
        let currentUserId: string | null = null;
        try {
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            currentUserId = payload.user_id ? String(payload.user_id) : null;
          }
        } catch {}
        
        const purchaseOwner = owners[ownedProduct.id];
        if (purchaseOwner && currentUserId && purchaseOwner !== currentUserId) {
          console.log('[RestorePurchases] Blocked: purchase belongs to user', purchaseOwner, 'not current user', currentUserId);
          alert('This subscription belongs to a different account. Please log in with the account that made the purchase.');
          return;
        }
        
        // Map product ID to tier
        const tierMap: Record<string, string> = {
          'com.wanderingsock.annual.basic': 'basic',
          'com.wanderingsock.annual.pro': 'pro',
          'com.wanderingsock.annual.business': 'business'
        };
        
        const tier = tierMap[ownedProduct.id];
        
        // Update subscription on backend
        try {
          const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
          console.log('[RestorePurchases] Calling sandbox-activate with tier:', tier, 'token exists:', !!token);
          
          const response = await fetch(`${API_BASE_URL}/api/subscription/sandbox-activate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              productId: ownedProduct.id,
              tier: tier
            })
          });
          
          console.log('[RestorePurchases] Sandbox activation response status:', response.status);

          if (response.ok) {
            alert(`Subscription restored: ${tier.toUpperCase()}`);
            if (onSuccess) {
              onSuccess();
            } else {
              window.location.reload();
            }
          } else {
            alert('Failed to activate subscription on server');
          }
        } catch (error) {
          alert('Network error while activating subscription');
        }
      } else {
        alert('No purchases to restore.');
      }
    } catch (error) {
      console.error('Restore error:', error);
      alert('Failed to restore purchases');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <button
      onClick={handleRestore}
      disabled={restoring}
      style={{
        padding: '8px 16px',
        backgroundColor: '#6c757d',
        color: 'var(--color-text-inverse)',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        cursor: restoring ? 'not-allowed' : 'pointer',
        opacity: restoring ? 0.6 : 1,
      }}
    >
      {restoring ? 'Restoring...' : 'Restore Purchases'}
    </button>
  );
};

export default RestorePurchasesButton;
